import { manufacturingFetch } from "@/lib/manufacturingClient";
import type { CogsMaterialsPatch } from "@/lib/manufacturing/cogsCatalogView";
import type {
  MfgPricingLayerBand,
  MfgPricingLayerColumn,
  MfgPricingLayerOp,
  MfgPricingLayerSourceKind,
  MfgPricingLayerSubjectKind,
  MfgPricingLayersGridResponse,
} from "@/lib/manufacturing/cogsTypes";

export type PricingLayersGridFilters = {
  plant_id?: string;
  /** Exact values; sent as comma-separated query params. */
  series?: string[];
  roof_style?: string[];
  size_band?: string[];
  include_material_uses?: boolean;
  include_named_variants?: boolean;
  /** Load the plant catalog so the browser can page and filter it. */
  catalog?: boolean;
  limit?: number;
  offset?: number;
};

function joinCsv(values?: string[]): string | undefined {
  if (!values?.length) return undefined;
  return values.map((v) => v.trim()).filter(Boolean).join(",");
}

function buildQuery(filters: PricingLayersGridFilters): string {
  const qs = new URLSearchParams();
  if (filters.plant_id) qs.set("plant_id", filters.plant_id);
  const series = joinCsv(filters.series);
  const roof = joinCsv(filters.roof_style);
  const size = joinCsv(filters.size_band);
  if (series) qs.set("series", series);
  if (roof) qs.set("roof_style", roof);
  if (size) qs.set("size_band", size);
  if (filters.include_material_uses) qs.set("include_material_uses", "true");
  if (filters.include_named_variants) qs.set("include_named_variants", "true");
  if (filters.catalog) qs.set("catalog", "true");
  if (typeof filters.limit === "number") qs.set("limit", String(filters.limit));
  if (typeof filters.offset === "number") qs.set("offset", String(filters.offset));
  const suffix = qs.toString();
  return suffix ? `?${suffix}` : "";
}

export async function listManufacturingPricingLayersGrid(filters: PricingLayersGridFilters = {}) {
  return manufacturingFetch<MfgPricingLayersGridResponse>(
    `/api/mfg/pricing/layers/grid${buildQuery(filters)}`,
    { method: "GET" },
  );
}

export async function refreshManufacturingPricingLayersMaterials(body: {
  recipe_ids: string[];
  plant_id?: string;
  limit?: number;
  /** Recalculate rows that already have a saved total. */
  force?: boolean;
}) {
  return manufacturingFetch<{
    ok: true;
    refreshed: number;
    failed: number;
    summaries: CogsMaterialsPatch[];
  }>("/api/mfg/pricing/layers/materials/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function listManufacturingPricingLayerColumns() {
  return manufacturingFetch<{
    ok: true;
    organization_id: string;
    columns: MfgPricingLayerColumn[];
    seeded: boolean;
  }>("/api/mfg/pricing/layers/columns", { method: "GET" });
}

export async function updateManufacturingPricingLayerColumn(body: {
  column_id: string;
  label?: string;
  band?: MfgPricingLayerBand;
  op?: MfgPricingLayerOp;
  source_kind?: MfgPricingLayerSourceKind;
  source_band?: MfgPricingLayerBand | null;
  sort_order?: number;
  source_module?: string | null;
  /** Set the column formula, or null to remove it. */
  calc?: { basis: string; rate_usd: number } | null;
  /** Markup reads the cell as a fraction added on top. Null clears that reading. */
  rate_kind?: "markup" | null;
  /** Products skips options and packages. All prices those rows too. */
  applies_to?: "products" | "all" | null;
  /** These recipes get the formula. Null prices every row the formula covers. */
  applies_recipe_ids?: string[] | null;
}) {
  return manufacturingFetch<{
    ok: true;
    column: MfgPricingLayerColumn;
    missing_size_count?: number;
  }>(
    "/api/mfg/pricing/layers/columns/update",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function saveManufacturingPricingBandRoundUp(body: {
  wholesale: number | null;
  msrp: number | null;
}) {
  return manufacturingFetch<{ ok: true; round_up: { wholesale: number | null; msrp: number | null } }>(
    "/api/mfg/pricing/layers/round-up",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function createManufacturingPricingLayerColumn(body: {
  band: MfgPricingLayerBand;
  key: string;
  label: string;
  op: MfgPricingLayerOp;
  source_kind?: MfgPricingLayerSourceKind;
  source_column_id?: string | null;
  source_band?: MfgPricingLayerBand | null;
  sort_order?: number;
  source_module?: string | null;
}) {
  return manufacturingFetch<{ ok: true; column: MfgPricingLayerColumn }>(
    "/api/mfg/pricing/layers/columns/create",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function upsertManufacturingPricingLayerValue(body: {
  column_id: string;
  subject_kind: MfgPricingLayerSubjectKind;
  subject_id: string;
  value_numeric: number;
  /** Omit / empty = org-wide; set = plant-specific override. */
  plant_id?: string;
}) {
  return manufacturingFetch<{ ok: true; value: { id: string; value_numeric: number } }>(
    "/api/mfg/pricing/layers/values/upsert",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function bulkSetManufacturingPricingLayerValues(body: {
  column_id: string;
  value_numeric: number;
  subjects: Array<{ subject_kind: MfgPricingLayerSubjectKind; subject_id: string }>;
  plant_id?: string;
}) {
  return manufacturingFetch<{ ok: true; updated_count: number }>(
    "/api/mfg/pricing/layers/values/bulk-set",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}
